import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, MessageCircle, Calendar, Building, Layers } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import WhatsAppFab from "../components/WhatsAppFab";
import SEO from "../components/SEO";
import { api } from "../lib/api";
import { useLang } from "../lib/i18n";
import { mediaUrl, isVideoFile, toEmbed } from "../lib/media";
import { optimizeImg } from "../lib/img";

function embedUrl(url) {
  return toEmbed(url || "");
}

export default function ProjectDetail() {
  const { slug } = useParams();
  const nav = useNavigate();
  const [project, setProject] = useState(null);
  const [similar, setSimilar] = useState([]);
  const [settings, setSettings] = useState(null);
  const { t, pick, lang } = useLang();

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get(`/projects/${slug}`).then((r) => setProject(r.data)).catch(() => nav("/"));
    api.get("/projects").then((r) => setSimilar(r.data));
    api.get("/settings").then((r) => setSettings(r.data));
  }, [slug, nav]);

  if (!project) return <div className="min-h-screen bg-[#08080D]" />;
  const wa = (settings?.whatsapp || "+905337819108").replace(/[^0-9]/g, "");
  const related = similar.filter((p) => p.slug !== project.slug && p.category === project.category).slice(0, 3);
  const video = embedUrl(project.video_url);

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "CreativeWork",
    "name": pick(project, "title"),
    "description": pick(project, "description"),
    "image": mediaUrl(project.cover_image),
    "creator": { "@type": "Organization", "name": "İxirli Reklam & Prodüksiyon" },
    "about": project.brand_name,
    "datePublished": project.project_date,
    "inLanguage": lang,
    ...(project.video_url && {
      "video": {
        "@type": "VideoObject",
        "name": pick(project, "title"),
        "description": pick(project, "description"),
        "thumbnailUrl": mediaUrl(project.cover_image),
        "uploadDate": project.project_date,
        "contentUrl": project.video_url,
      },
    }),
  };

  return (
    <>
      <SEO
        title={`${pick(project, "title")} — İxirli Prodüksiyon`}
        description={pick(project, "description")}
        image={mediaUrl(project.cover_image)}
        path={`/projects/${project.slug}`}
        type="article"
        lang={lang}
        publishedAt={project.project_date}
        jsonLd={jsonLd}
        keywords={[project.brand_name, project.category]}
      />
      <Header />
      <main className="pt-28 pb-20">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <button
            data-testid="back-home"
            onClick={() => nav(-1)}
            className="inline-flex items-center gap-2 text-white/60 hover:text-[#A855F7] text-sm mb-10 transition-colors"
          >
            <ArrowLeft size={14} /> {lang === "tr" ? "Geri" : "Back"}
          </button>

          <div className="max-w-4xl">
            <div className="font-mono-ui text-[10px] tracking-[0.3em] text-[#A855F7] uppercase mb-4">
              {project.brand_name}
            </div>
            <h1 className="font-display text-5xl md:text-7xl uppercase tracking-tighter text-white leading-[0.9]">
              {pick(project, "title")}
            </h1>
            <p className="mt-8 text-white/60 text-lg max-w-2xl">{pick(project, "description")}</p>
          </div>

          {project.video_url ? (
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
              className="mt-16 rounded-3xl overflow-hidden border border-white/5 glow-purple aspect-video"
            >
              {isVideoFile(project.video_url) ? (
                <video
                  src={mediaUrl(project.video_url)}
                  controls
                  poster={mediaUrl(project.cover_image)}
                  className="w-full h-full object-cover bg-black"
                />
              ) : (
                <iframe
                  src={embedUrl(project.video_url)}
                  title={project.title_tr}
                  allowFullScreen
                  className="w-full h-full"
                />
              )}
            </motion.div>
          ) : (
            <div className="mt-16 rounded-3xl overflow-hidden border border-white/5 glow-purple">
              <img src={optimizeImg(project.cover_image, 1400, 80)} alt="" loading="lazy" decoding="async" className="w-full aspect-video object-cover" />
            </div>
          )}

          <div className="mt-16 grid md:grid-cols-4 gap-8 border-y border-white/10 py-10">
            <div>
              <div className="text-[10px] tracking-widest text-[#A855F7] font-mono-ui uppercase mb-2 flex items-center gap-2"><Building size={12} /> {t("client")}</div>
              <div className="text-white">{project.brand_name}</div>
            </div>
            <div>
              <div className="text-[10px] tracking-widest text-[#A855F7] font-mono-ui uppercase mb-2 flex items-center gap-2"><Layers size={12} /> {t("service")}</div>
              <div className="text-white">{project.category}</div>
            </div>
            <div>
              <div className="text-[10px] tracking-widest text-[#A855F7] font-mono-ui uppercase mb-2 flex items-center gap-2"><Calendar size={12} /> {t("date")}</div>
              <div className="text-white">{project.project_date}</div>
            </div>
            <div>
              <a
                data-testid="project-whatsapp"
                href={`https://wa.me/${wa}?text=${encodeURIComponent(`${project.brand_name} — ${pick(project, "title")} projesi hakkında teklif almak istiyorum.`)}`}
                target="_blank" rel="noreferrer"
                className="btn-primary text-[11px]"
              >
                <MessageCircle size={14} /> WhatsApp
              </a>
            </div>
          </div>

          {!!(project.gallery || []).length && (
            <div className="mt-16">
              <h3 className="font-display text-3xl uppercase tracking-tight text-white mb-6">Gallery</h3>
              <div className="grid md:grid-cols-2 gap-6">
                {project.gallery.map((g, i) => (
                  <img key={i} src={optimizeImg(g, 1200, 75)} alt="" loading="lazy" decoding="async" className="w-full aspect-video object-cover rounded-2xl border border-white/5" />
                ))}
              </div>
            </div>
          )}

          {project.before_image && project.after_image && (
            <div className="mt-16">
              <h3 className="font-display text-3xl uppercase tracking-tight text-white mb-6">{t("before_after")}</h3>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="relative">
                  <img src={optimizeImg(project.before_image, 1200, 75)} alt="before" loading="lazy" decoding="async" className="w-full aspect-video object-cover rounded-2xl border border-white/5" />
                  <span className="absolute top-4 left-4 px-3 py-1 rounded-full bg-black/60 text-white text-xs uppercase tracking-widest">Before</span>
                </div>
                <div className="relative">
                  <img src={optimizeImg(project.after_image, 1200, 75)} alt="after" loading="lazy" decoding="async" className="w-full aspect-video object-cover rounded-2xl border border-white/5" />
                  <span className="absolute top-4 left-4 px-3 py-1 rounded-full bg-[#A855F7] text-white text-xs uppercase tracking-widest">After</span>
                </div>
              </div>
            </div>
          )}

          {related.length > 0 && (
            <div className="mt-24">
              <h3 className="font-display text-3xl uppercase tracking-tight text-white mb-8">{t("similar_projects")}</h3>
              <div className="grid md:grid-cols-3 gap-6">
                {related.map((r) => (
                  <Link
                    key={r.id}
                    to={`/projects/${r.slug}`}
                    className="group block rounded-2xl overflow-hidden border border-white/5 hover:border-[#A855F7]/50 transition-colors"
                  >
                    <div className="aspect-[4/5] overflow-hidden">
                      <img src={optimizeImg(r.cover_image, 600, 72)} alt="" loading="lazy" decoding="async" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    </div>
                    <div className="p-4">
                      <div className="text-[10px] tracking-widest uppercase text-[#A855F7] font-mono-ui">{r.brand_name}</div>
                      <div className="text-white mt-1">{pick(r, "title")}</div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </main>
      <Footer />
      <WhatsAppFab />
    </>
  );
}
